import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppContextStore } from '@/stores/appContext'
import type { Child } from '@/models/Child'

/**
 * L'enfant en contexte, partagé par les trois surfaces par enfant (Tâches,
 * Récompenses, Habitudes).
 *
 * Avant, chaque page avait sa propre sélection : Tâches la lisait dans l'URL
 * (`?childId=`), Récompenses et Habitudes gardaient chacune sa clé de
 * sessionStorage. Passer d'un onglet à l'autre pouvait donc changer d'enfant
 * sans que personne ne l'ait demandé, ce qui est exactement ce qu'une barre
 * unique ne doit plus laisser arriver.
 *
 * Ordre de résolution, du plus explicite au plus tolérant :
 *   1. la sélection faite À LA MAIN dans cette session ;
 *   2. `?childId=` de l'URL, la seule forme partageable (deep link, email) ;
 *   3. l'enfant courant du store ;
 *   4. la dernière sélection retenue par la session, après une relance ;
 *   5. le premier enfant du foyer.
 *
 * Un id qui ne correspond à aucun enfant chargé (enfant supprimé, vieux lien)
 * retombe donc sur le suivant, et à défaut sur le premier de la liste, plutôt
 * que de laisser la page vide.
 *
 * ⚠️ **L'URL est un MIROIR de la sélection, jamais sa source.** La sélection a
 * d'abord été portée par `?childId=` seul, et le picker paraissait mort : le
 * tap déclenchait un `router.replace` qui traverse la garde globale, laquelle
 * attend `childAuthStore.initSession()`, `authStore.waitForAuth()` et
 * `onboarding.init()` à CHAQUE navigation. Rien ne bougeait à l'écran tant que
 * cette chaîne n'avait pas abouti, et rien du tout si une garde redirigeait.
 * Choisir un enfant n'est pas une navigation : ça doit se voir dans l'image
 * qui suit le doigt. L'URL est mise à jour derrière, pour les liens partagés.
 */

const STORAGE_KEY = 'harmonia:selectedChildId'

/** Les routes qui portent `?childId=` dans leur URL. Ailleurs on n'écrit rien. */
const PER_CHILD_PATHS = new Set([
  '/tabs/tasks',
  '/tabs/rewards',
  '/tabs/stats',
])

export function isPerChildPath(path: string): boolean {
  return PER_CHILD_PATHS.has(path)
}

/**
 * Lu UNE fois, au chargement du module, et tenu à jour par `selectChild`.
 *
 * Le lire depuis le `computed` marchait, mais chaque recalcul payait un accès
 * synchrone au stockage, et surtout la valeur n'était pas réactive : une
 * écriture n'aurait jamais rafraîchi la vue. Ici c'est un simple repli de
 * démarrage, il n'a aucune raison d'être relu ensuite.
 */
let storedId: string | null = (() => {
  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
})()

function writeStored(id: string): void {
  storedId = id
  try {
    sessionStorage.setItem(STORAGE_KEY, id)
  } catch {
    /* stockage indisponible : la sélection vit alors le temps de la session */
  }
}

/**
 * Résout l'enfant à afficher. Pur, donc testable sans routeur ni store : c'est
 * ici que vit toute la règle de repli.
 */
export function resolveSelectedChild<T extends { id: string }>(
  children: T[],
  candidates: Array<string | null | undefined>,
): T | null {
  if (!children.length) return null
  for (const id of candidates) {
    if (!id) continue
    const found = children.find((c) => c.id === id)
    if (found) return found
  }
  return children[0]
}

/**
 * La sélection de cette session, écrite au tap et lue tout de suite. Au niveau
 * du module : les quatre surfaces qui la lisent doivent voir la même valeur,
 * et elle doit survivre au démontage d'une page.
 */
const selectedId = ref<string | null>(null)

export function useSelectedChild() {
  const appContext = useAppContextStore()
  const route = useRoute()
  const router = useRouter()

  const children = computed<Child[]>(() => (appContext.availableChildren ?? []) as Child[])

  const selectedChild = computed<Child | null>(() =>
    resolveSelectedChild(children.value, [
      selectedId.value,
      route.query.childId as string | undefined,
      appContext.currentChildId,
      storedId,
    ]),
  )

  // Un lien profond doit pouvoir imposer son enfant même après une sélection
  // à la main : une notification qui pointe un enfant précis ouvre bien SON
  // écran. On adopte donc toute valeur d'URL qui change, sans jamais effacer la
  // sélection quand elle disparaît (passer sur Réglages ne l'oublie pas).
  // `immediate`, parce qu'une notification peut ouvrir l'app directement sur
  // `/tabs/tasks?childId=`.
  watch(
    () => route.query.childId,
    (id) => {
      if (typeof id !== 'string' || !id) return
      if (id !== selectedId.value) selectedId.value = id
    },
    { immediate: true },
  )

  const selectedChildId = computed<string | null>(() => selectedChild.value?.id ?? null)

  /**
   * Point d'écriture unique. Le store fait foi pour l'app, l'URL le reflète sur
   * les surfaces par enfant, et la session garde la trace pour la prochaine
   * ouverture.
   *
   * `replace` et non `push` : changer d'enfant n'est pas une navigation, et une
   * entrée d'historique par tap rendrait le retour absurde (il faudrait
   * remonter toute la liste des enfants consultés pour quitter la page).
   */
  function selectChild(child: Child | string): void {
    const id = typeof child === 'string' ? child : child.id
    const target = children.value.find((c) => c.id === id)
    if (!target) return

    // D'abord ce qui se voit, tout de suite et sans rien attendre.
    selectedId.value = id
    appContext.setCurrentChild(target as never)
    writeStored(id)

    // Puis le miroir, en tâche de fond : il rend le lien partageable et
    // survivant au rechargement. Son échec ne coûte que ça.
    if (isPerChildPath(route.path) && route.query.childId !== id) {
      void router.replace({ path: route.path, query: { ...route.query, childId: id } })
    }
  }

  return { children, selectedChild, selectedChildId, selectChild }
}
